import {
  ActiveTriggerTypesRequest,
  AvailableTriggerFiltersRequest,
  GetAttributionRequest,
  GetAttributionResponse,
  GetLocationsRequest,
  TriggerFilterOptions,
  TriggerFilterOptionsRequest,
  TriggerFilterOptionsResponse,
} from '@weave/schema-gen-ts/dist/schemas/messaging/etl/amp/v1/service.pb';
import { useQueries, useQuery, UseQueryOptions } from 'react-query';
import { SchemaMetlAmp } from './services';

export const queryKeys = {
  base: 'messages',
  activeTriggerTypes: (request: ActiveTriggerTypesRequest) => [queryKeys.base, 'active-trigger-types', request],
  getAppointmentsCompleted: (request: GetAttributionRequest) => [queryKeys.base, 'appointments-completed', request],
  getAppointmentsConfirmed: (request: GetAttributionRequest) => [queryKeys.base, 'appointments-confirmed', request],
  getAppointmentsScheduled: (request: GetAttributionRequest) => [queryKeys.base, 'appointments-scheduled', request],
  getAvailableTriggerFilters: (request: AvailableTriggerFiltersRequest) => [
    queryKeys.base,
    'available-trigger-filters',
    request,
  ],
  getLocations: (request: GetLocationsRequest) => [queryKeys.base, 'locations', request],
  getMessagesReplied: (request: GetAttributionRequest) => [queryKeys.base, 'messages-replied', request],
  getMessagesSent: (request: GetAttributionRequest) => [queryKeys.base, 'messages-sent', request],
  triggerFilterOptions: (request: TriggerFilterOptionsRequest) => [queryKeys.base, 'triggerFilterOptions', request],
};

const defaultOptions = {
  retry: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
};

type AttributionQueryOptions = UseQueryOptions<
  GetAttributionResponse,
  unknown,
  GetAttributionResponse,
  (string | GetAttributionRequest)[]
>;

export const useActiveTriggerTypes = (request: ActiveTriggerTypesRequest) =>
  useQuery({
    queryKey: queryKeys.activeTriggerTypes(request),
    queryFn: () => SchemaMetlAmp.ActiveTriggerTypes(request),
    select: (response) => response.hasAppointmentEnteredAt,
    enabled: !!request.locationIds && request.locationIds.length > 0,
  });

export const useAvailableTriggerFilters = (request: AvailableTriggerFiltersRequest) =>
  useQuery({
    queryKey: queryKeys.getAvailableTriggerFilters(request),
    queryFn: () => SchemaMetlAmp.AvailableTriggerFilters(request),
    select: (data) => data.availableFilters,
    enabled: !!request.locationIds?.length && !!request.triggerType,
  });

export const useGetAppointmentsCompleted = (request: GetAttributionRequest, options: AttributionQueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getAppointmentsCompleted(request),
    queryFn: () => SchemaMetlAmp.GetAppointmentsCompleted(request),
    ...options,
  });

export const useGetAppointmentsConfirmed = (request: GetAttributionRequest, options: AttributionQueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getAppointmentsConfirmed(request),
    queryFn: () => SchemaMetlAmp.GetAppointmentsConfirmed(request),
    ...options,
  });

export const useGetAppointmentsScheduled = (request: GetAttributionRequest, options: AttributionQueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getAppointmentsScheduled(request),
    queryFn: () => SchemaMetlAmp.GetAppointmentsScheduled(request),
    ...options,
  });

export const useGetLocation = (request: GetLocationsRequest) =>
  useQuery({
    queryKey: queryKeys.getLocations(request),
    queryFn: () => SchemaMetlAmp.GetLocations(request),
    select: (data) => data.locations,
    enabled: !!request.orgId,
  });

export const useGetMessagesReplied = (request: GetAttributionRequest, options: AttributionQueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getMessagesReplied(request),
    queryFn: () => SchemaMetlAmp.GetMessagesReplied(request),
    ...options,
  });

export const useGetMessagesSent = (request: GetAttributionRequest, options: AttributionQueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getMessagesSent(request),
    queryFn: () => SchemaMetlAmp.GetMessagesSent(request),
    enabled: !!request.orgId && !!request.locationIds?.length,
    ...options,
  });

export const useTriggerFilterOptionsByFilterType = (
  requests: {
    request: TriggerFilterOptionsRequest;
    options?: UseQueryOptions<TriggerFilterOptionsResponse, unknown, Record<string, TriggerFilterOptions> | undefined>;
  }[]
) => {
  const results = useQueries(
    requests.map(({ request, options }) => ({
      queryKey: queryKeys.triggerFilterOptions(request),
      queryFn: () => SchemaMetlAmp.TriggerFilterOptions(request),
      select: (data: TriggerFilterOptionsResponse) => data?.options,
      ...options,
    }))
  );

  return {
    isAllFetched: results.every((result) => result.isFetched),
    data: results.map((result) => result.data),
  };
};

export const useTriggerFilterOptions = (
  request: TriggerFilterOptionsRequest,
  options: UseQueryOptions<Record<string, TriggerFilterOptions> | undefined, unknown, TriggerFilterOptionsResponse> = {}
) =>
  useQuery({
    queryKey: queryKeys.triggerFilterOptions(request),
    queryFn: (queryFunctionContext) =>
      SchemaMetlAmp.TriggerFilterOptions(request, { signal: queryFunctionContext.signal }),
    select: (data) => data.options,
    enabled: (options.hasOwnProperty('enabled') ? options.enabled : true) && !!request.locationIds?.length,
  });
